import { useCallback } from 'react'
import { atom, useAtom } from 'jotai'
import type { PendingAttachment } from './types'

/**
 * ATL-2068 — the support chat composer's draft: the typed text and the picked attachments.
 *
 * Held in memory OUTSIDE the composer, so it survives the chat panel being closed and opened
 * again (until the app restarts). Deliberately not persisted: a web attachment is a `File`/`blob:`
 * that AsyncStorage cannot hold, and a restored draft without its files would be a lie.
 *
 * The draft carries its owner (the signed-in user's id). A draft written by somebody else, i.e.
 * left behind by a previous sign-in on the same device, reads as empty and is replaced on the
 * first edit: one merchant's unsent message must never appear in another's composer.
 */
export interface SupportDraft {
  owner: string | null
  text: string
  pending: PendingAttachment[]
}

export const supportDraftAtom = atom<SupportDraft | null>(null)

/**
 * A composer send is in flight. Outside the component for the same reason as the draft: a chat
 * closed and reopened mid-send must not start a second send, or the two failures would restore
 * their messages in the wrong order. Read it through the store (synchronous), not a render value.
 */
export const supportSendInFlightAtom = atom(false)

type Update<T> = T | ((current: T) => T)

const apply = <T>(update: Update<T>, current: T): T =>
  typeof update === 'function' ? (update as (c: T) => T)(current) : update

/** The draft as `owner` sees it: someone else's draft is no draft. */
export const draftFor = (draft: SupportDraft | null, owner: string | null): SupportDraft =>
  draft && draft.owner === owner ? draft : { owner, text: '', pending: [] }

export interface UseSupportDraft {
  text: string
  pending: PendingAttachment[]
  setText: (update: Update<string>) => void
  setPending: (update: Update<PendingAttachment[]>) => void
}

export const useSupportDraft = (owner: string | null): UseSupportDraft => {
  const [draft, setDraft] = useAtom(supportDraftAtom)
  const current = draftFor(draft, owner)

  // Functional updates read the atom's latest value, not this render's: a restore that lands
  // after the composer was edited (or unmounted) must merge with what is there now.
  const setText = useCallback(
    (update: Update<string>) =>
      setDraft((prev) => {
        const base = draftFor(prev, owner)
        return { ...base, text: apply(update, base.text) }
      }),
    [owner, setDraft]
  )
  const setPending = useCallback(
    (update: Update<PendingAttachment[]>) =>
      setDraft((prev) => {
        const base = draftFor(prev, owner)
        return { ...base, pending: apply(update, base.pending) }
      }),
    [owner, setDraft]
  )

  return { text: current.text, pending: current.pending, setText, setPending }
}
